import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Checkbox,
  Slider,
  Collapse,
  useTheme,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import * as pdfjsLib from "pdfjs-dist";
import CloseIcon from "@mui/icons-material/Close";
import CustomizedResumes from "./CustomizedResumes";
import ResumeVersions from "./ResumeVersions";
import GmailMailboxesPanel from "../components/GmailMailboxesPanel";
import GmailSenderBlacklistDrawer from "../components/GmailSenderBlacklistDrawer";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import GoogleIcon from "@mui/icons-material/Google";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SendIcon from "@mui/icons-material/Send";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from "@mui/icons-material/FilterList";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CategoryIcon from "@mui/icons-material/Category";
import PsychologyIcon from "@mui/icons-material/Psychology";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import TitleIcon from "@mui/icons-material/Title";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import DoNotTouchIcon from "@mui/icons-material/DoNotTouch";

const RESUME_GROUPS_COLLAPSED_KEY = "resumeGroupsCollapsed";
const STICKY_TABS_HEIGHT = 56;

const EMPTY_PERSONAL_INFO = {
  name: "",
  title: "",
  email: "",
  phone_number: "",
  location: "",
  linkedin: "",
};

function asResumeArray(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
}

const SectionTitleWithInfo = ({ title, tip, variant = "h5" }) => (
  <Box display="flex" alignItems="center" gap={0.5}>
    <Typography variant={variant} gutterBottom sx={{ mb: 0 }}>
      {title}
    </Typography>
    {tip ? (
      <Tooltip
        title={tip}
        arrow
        placement="top"
        slotProps={{
          tooltip: {
            sx: { maxWidth: 360 },
          },
        }}
      >
        <IconButton
          size="small"
          aria-label={`More information about ${title}`}
          sx={{ color: "text.secondary", p: 0.25 }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null}
  </Box>
);

// Set the worker source using a local path instead of CDN
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";


const locationOptions = [
  { value: "all", label: "Anywhere", count: 0 },
  { value: "USA", label: "USA", count: 0 },
  { value: "Canada", label: "Canada", count: 0 },
  { value: "Europe", label: "Europe", count: 0 },
  { value: "Asia", label: "Asia", count: 0 },
  { value: "LATAM", label: "LATAM", count: 0 },
  { value: "Australia", label: "Australia", count: 0 },
  { value: "Africa", label: "Africa", count: 0 },
  { value: "Anonymous", label: "Anonymous", count: 0 },
];

const behaviorOptions = [
  "Remote Job",
  "Non Remote Job",
  "Email Found",
  "Login Required",
  "Verification Required",
  "Other Relevant",
  "Expired",
  "Irrelevant",
  "Uncategorized"
];

const buildResumeContentPayload = (resume) => ({
  personal_info: resume.personal_info,
  profile: resume.profile,
  education: resume.education,
  experience: resume.experience,
  summarized_experience: resume.summarized_experience || [],
  skillset: resume.skillset,
  additional_info: resume.additional_info || "",
});

const getVisibleBidderId = (resume) => (resume?.visibleToBidders || [])[0] || "";

const resolveAutoCustomizeResume = (resume) =>
  Boolean(resume?.auto_customize_resume ?? resume?.auto_customize_for_mail);


const Resume = () => {
  const [resumes, setResumes] = useState([]);
  const [selectedResumeIndex, setSelectedResumeIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedResume, setEditedResume] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resumeToDelete, setResumeToDelete] = useState(null);
  const [migrateGmailOnDelete, setMigrateGmailOnDelete] = useState(true);
  const [pageTab, setPageTab] = useState(0);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [customizedResumesOpen, setCustomizedResumesOpen] = useState(false);
  const [autoEmailEnabled, setAutoEmailEnabled] = useState(false);
  const [coverLetterTitle, setCoverLetterTitle] = useState("");
  const [coverLetterContent, setCoverLetterContent] = useState("");
  const [savingAutoEmail, setSavingAutoEmail] = useState(false);
  const [savingAutoEmailSettings, setSavingAutoEmailSettings] = useState(false);
  const [generatingCoverLetter, setGeneratingCoverLetter] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailEmail, setGmailEmail] = useState("");
  const [generatingAuthUrl, setGeneratingAuthUrl] = useState(false);
  const [authUrl, setAuthUrl] = useState("");
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [removingGmail, setRemovingGmail] = useState(false);
  const [gmailStatusHovered, setGmailStatusHovered] = useState(false);
  const [gmailTokenExpiryDate, setGmailTokenExpiryDate] = useState(null);
  const [gmailCleanup, setGmailCleanup] = useState(false);
  const [loadingCleanup, setLoadingCleanup] = useState(false);
  const [cleanupError, setCleanupError] = useState("");
  const [savingGmailCleanup, setSavingGmailCleanup] = useState(false);
  const [senderBlacklistDrawerOpen, setSenderBlacklistDrawerOpen] = useState(false);
  const [autoCustomizeResume, setAutoCustomizeResume] = useState(false);
  const [savingAutoCustomizeResume, setSavingAutoCustomizeResume] = useState(false);
  const [scheduledEmails, setScheduledEmails] = useState(null);
  const [loadingScheduledEmails, setLoadingScheduledEmails] = useState(false);
  const [scheduledEmailsError, setScheduledEmailsError] = useState("");
  const { user } = useAuth();
  const [editingNicknameId, setEditingNicknameId] = useState(null);
  const [nicknameInput, setNicknameInput] = useState("");
  const [savingNickname, setSavingNickname] = useState(false);
  
  // Google token selection states
  const [availableGoogleTokens, setAvailableGoogleTokens] = useState([]);
  const [selectedGoogleToken, setSelectedGoogleToken] = useState(0);
  const [loadingGoogleTokens, setLoadingGoogleTokens] = useState(false);

  // Add this state variable with the other useState declarations (around line 95)
  const [savingResume, setSavingResume] = useState(false);
  const [savingResumeEnabled, setSavingResumeEnabled] = useState(false);

  const [resumeGroups, setResumeGroups] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupNameInput, setGroupNameInput] = useState("");
  const [savingGroup, setSavingGroup] = useState(false);
  const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [draggingResumeId, setDraggingResumeId] = useState(null);
  const [dropTargetKey, setDropTargetKey] = useState(null);

  // Define selectedResume here so it's available throughout the component
  const selectedResume = resumes[selectedResumeIndex] || null;
  const isSelectedResumeEnabled = selectedResume?.is_enabled !== false;

  // Add new state for filters
  const [locationFilter, setLocationFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [behaviorFilter, setBehaviorFilter] = useState(["all"]);
  const [queryDateLimit, setQueryDateLimit] = useState([-1]);

  // Date limit options - reduced to 5 options
  const dateLimitOptions = [
    { value: -1, label: "All" },
    { value: 1, label: "1 day" },
    { value: 3, label: "3 days" },
    { value: 7, label: "7 days" },
    { value: 30, label: "30 days" },
  ];

  // Add new state for filters dialog
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    location: [],
    category: [],
    behavior: ["all"],
    dateLimit: [-1]
  });

  // State for team categories
  const [teamCategories, setTeamCategories] = useState([]);

  // Add new state for bidder visibility
  const [visibleToBidder, setVisibleToBidder] = useState("");
  const [teamBidders, setTeamBidders] = useState([]);
  const [savingBidderVisibility, setSavingBidderVisibility] = useState(false);

  // Add function to handle opening filters dialog
  const handleOpenFiltersDialog = () => {
    setTempFilters({
      location: [...locationFilter],
      category: [...categoryFilter],
      behavior: [...behaviorFilter],
      dateLimit: [...queryDateLimit]
    });
    setFiltersDialogOpen(true);
  };

  // Add function to handle saving filters
  const handleSaveFilters = async () => {
    if (!selectedResume) return;
    
    try {
      // Save filters to backend using the existing auto-email-settings endpoint
      await axios.put(
        `${process.env.REACT_APP_API_URL}/resumes/${selectedResume._id}/auto-email-settings`,
        {
          cover_letter_title: coverLetterTitle,
          cover_letter_content: coverLetterContent,
          location_filter: tempFilters.location,
          category_filter: tempFilters.category,
          behavior_filter: tempFilters.behavior,
          confidence_range: [0.3, 1],
          query_date_limit: tempFilters.dateLimit,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Update local state
      setLocationFilter([...tempFilters.location]);
      setCategoryFilter([...tempFilters.category]);
      setBehaviorFilter([...tempFilters.behavior]);
      setQueryDateLimit([...tempFilters.dateLimit]);
      
      // Update the local resume data
      const updatedResumes = [...resumes];
      updatedResumes[selectedResumeIndex] = {
        ...updatedResumes[selectedResumeIndex],
        auto_email_filters: {
          ...updatedResumes[selectedResumeIndex].auto_email_filters,
          location_filter: tempFilters.location,
          category_filter: tempFilters.category,
          behavior_filter: tempFilters.behavior,
          query_date_limit: tempFilters.dateLimit,
        }
      };
      setResumes(updatedResumes);
      
      setFiltersDialogOpen(false);
      toast.success("Filters updated successfully!");
    } catch (err) {
      console.error('Failed to save filters:', err);
      toast.error("Failed to save filters. Please try again.");
    }
  };

  // Add function to handle canceling filters
  const handleCancelFilters = () => {
    setFiltersDialogOpen(false);
  };

  const sortResumesByEnabled = (resumeList) => {
    return [...resumeList].sort((a, b) => {
      const aDisabled = a.is_enabled === false ? 1 : 0;
      const bDisabled = b.is_enabled === false ? 1 : 0;
      return aDisabled - bDisabled;
    });
  };

  const fetchResumes = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/resumes`
      );
      if (Array.isArray(response.data) && response.data.length > 0) {
        const formattedResumes = response.data.map((resumeData) => {
          const content = resumeData.content || {};
          const experienceEntries = asResumeArray(content.experience).map((exp, index) => ({
            id: index + 1,
            company: exp.company,
            position: exp.position,
            duration: exp.duration,
            description: exp.description,
          }));

          const summarizedExperienceEntries = asResumeArray(content.summarized_experience).map((exp, index) => ({
            id: index + 1,
            company: exp.company,
            position: exp.position,
            duration: exp.duration,
            description: exp.description,
          }));

          const educationEntries = asResumeArray(content.education).map((edu, index) => ({
            id: index + 1,
            institution: edu.institution,
            degree: edu.degree,
            year: edu.year,
          }));

          const skillEntries = asResumeArray(content.skillset).map((skill, index) => ({
            id: index + 1,
            category: skill.category,
            skills: skill.skills,
          }));

          return {
            _id: resumeData._id,
            owner: resumeData.owner,
            nickname: resumeData.nickname || null,
            personal_info: { ...EMPTY_PERSONAL_INFO, ...(content.personal_info || {}) },
            profile: content.profile,
            education: educationEntries,
            experience: experienceEntries,
            summarized_experience: summarizedExperienceEntries,
            skillset: skillEntries,
            additional_info: content.additional_info || "",
            path: resumeData.path,
            auto_email_application: resumeData.auto_email_application || false,
            cover_letter_title: resumeData.cover_letter?.title || "",
            cover_letter_content: resumeData.cover_letter?.content || "",
            auto_customize_resume: resolveAutoCustomizeResume(resumeData),
            gmail_credentials_setup:
              resumeData.gmail_credentials_setup || false,
            gmail_email: resumeData.gmail_email || "",
            gmail_status: resumeData.gmail_status || {
              connected: false,
              email: "",
              hasToken: false,
              tokenExpiryDate: null,
              isNearExpiry: false,
              tokenIndex: null,
            },
            gmail_cleanup_status: resumeData.gmail_cleanup_status || {
              gmail_auto_cleanup: false,
            },
            gmail_forwarding_status: resumeData.gmail_forwarding_status || {
              forward_to: null,
              enabled: false,
              verification_status: null,
              can_configure: false,
            },
            is_enabled: resumeData.is_enabled !== false,
            showToBidder: Boolean(getVisibleBidderId(resumeData)),
            auto_email_filters: resumeData.auto_email_filters || {
              location_filter: [],
              category_filter: [],
              behavior_filter: ["all"],
              confidence_range: [0.3, 1],
              query_date_limit: [-1]
            },
            visibleToBidders: getVisibleBidderId(resumeData)
              ? [getVisibleBidderId(resumeData)]
              : [],
            versions: resumeData.versions || [],
            group_id: resumeData.group_id || null,
          };
        });

        setResumes(sortResumesByEnabled(formattedResumes));
      } else {
        setError("No resume data found");
      }
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch resumes");
      setLoading(false);
    }
  };

  const fetchResumeGroups = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/resume-groups`);
      setResumeGroups(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to fetch resume groups:", err);
    }
  };

  const loadCollapsedGroups = () => {
    if (!user?._id) return;
    try {
      const stored = localStorage.getItem(`${RESUME_GROUPS_COLLAPSED_KEY}_${user._id}`);
      setCollapsedGroups(stored ? JSON.parse(stored) : {});
    } catch {
      setCollapsedGroups({});
    }
  };

  const toggleGroupCollapsed = (groupId) => {
    const key = groupId || "ungrouped";
    setCollapsedGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (user?._id) {
        localStorage.setItem(`${RESUME_GROUPS_COLLAPSED_KEY}_${user._id}`, JSON.stringify(next));
      }
      return next;
    });
  };

  const isGroupCollapsed = (groupId) => {
    const key = groupId || "ungrouped";
    return !!collapsedGroups[key];
  };

  const getResumesForGroup = (groupId) => {
    return sortResumesByEnabled(
      resumes.filter((resume) => {
        if (!groupId) {
          return !resume.group_id;
        }
        return String(resume.group_id) === String(groupId);
      })
    );
  };

  const handleOpenCreateGroup = () => {
    setEditingGroup(null);
    setGroupNameInput("");
    setGroupDialogOpen(true);
  };

  const handleOpenEditGroup = (group) => {
    setEditingGroup(group);
    setGroupNameInput(group.name);
    setGroupDialogOpen(true);
  };

  const handleCloseGroupDialog = () => {
    setGroupDialogOpen(false);
    setEditingGroup(null);
    setGroupNameInput("");
  };

  const handleSaveGroup = async () => {
    const name = groupNameInput.trim();
    if (!name) {
      toast.error("Group name is required");
      return;
    }

    setSavingGroup(true);
    try {
      if (editingGroup) {
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/resume-groups/${editingGroup._id}`,
          { name }
        );
        setResumeGroups((prev) =>
          prev.map((group) => (group._id === editingGroup._id ? response.data : group))
        );
        toast.success("Group updated successfully");
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/resume-groups`,
          { name }
        );
        setResumeGroups((prev) => [...prev, response.data]);
        toast.success("Group created successfully");
      }
      handleCloseGroupDialog();
    } catch (err) {
      console.error("Failed to save resume group:", err);
      toast.error("Failed to save group. Please try again.");
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroupClick = (group) => {
    setGroupToDelete(group);
    setDeleteGroupDialogOpen(true);
  };

  const handleDeleteGroupConfirm = async () => {
    if (!groupToDelete) return;

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/resume-groups/${groupToDelete._id}`);
      setResumeGroups((prev) => prev.filter((group) => group._id !== groupToDelete._id));
      setResumes((prev) =>
        prev.map((resume) =>
          String(resume.group_id) === String(groupToDelete._id)
            ? { ...resume, group_id: null }
            : resume
        )
      );
      toast.success("Group deleted successfully");
    } catch (err) {
      console.error("Failed to delete resume group:", err);
      toast.error("Failed to delete group. Please try again.");
    } finally {
      setDeleteGroupDialogOpen(false);
      setGroupToDelete(null);
    }
  };

  const getDropTargetKey = (groupId) => (groupId ? `group-${groupId}` : "ungrouped");

  const expandGroup = (groupId) => {
    if (!groupId || !isGroupCollapsed(groupId)) return;
    setCollapsedGroups((prev) => {
      const next = { ...prev, [groupId]: false };
      if (user?._id) {
        localStorage.setItem(`${RESUME_GROUPS_COLLAPSED_KEY}_${user._id}`, JSON.stringify(next));
      }
      return next;
    });
  };

  const handleResumeDragStart = (event, resumeId) => {
    event.dataTransfer.setData("application/x-resume-id", resumeId);
    event.dataTransfer.effectAllowed = "move";
    setDraggingResumeId(resumeId);
  };

  const handleResumeDragEnd = () => {
    setDraggingResumeId(null);
    setDropTargetKey(null);
  };

  const handleGroupDragOver = (event, groupId) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDropTargetKey(getDropTargetKey(groupId));
  };

  const handleGroupDrop = async (event, groupId) => {
    event.preventDefault();
    const resumeId = event.dataTransfer.getData("application/x-resume-id");
    setDropTargetKey(null);
    setDraggingResumeId(null);
    if (!resumeId) return;

    const resume = resumes.find((item) => item._id === resumeId);
    const targetGroupId = groupId || null;
    const currentGroupId = resume?.group_id || null;
    if (String(currentGroupId) === String(targetGroupId || "")) return;

    if (groupId) {
      expandGroup(groupId);
    }

    await handleAssignResumeToGroup(resumeId, targetGroupId);
  };

  const getGroupDropZoneSx = (groupId) => ({
    ...(dropTargetKey === getDropTargetKey(groupId)
      ? {
          backgroundColor: "primary.light",
          outline: "2px dashed",
          outlineColor: "primary.main",
        }
      : {}),
  });

  const handleAssignResumeToGroup = async (resumeId, groupId) => {
    const resume = resumes.find((item) => item._id === resumeId);
    const targetGroupId = groupId || null;
    const currentGroupId = resume?.group_id || null;
    if (String(currentGroupId) === String(targetGroupId || "")) return;

    const previousGroupId = resume?.group_id || null;
    setResumes((prev) =>
      prev.map((item) =>
        item._id === resumeId ? { ...item, group_id: targetGroupId } : item
      )
    );

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/resumes/${resumeId}/group`,
        { group_id: groupId || null }
      );
      setResumes((prev) =>
        prev.map((item) =>
          item._id === resumeId
            ? { ...item, group_id: response.data.group_id || null }
            : item
        )
      );
    } catch (err) {
      console.error("Failed to assign resume to group:", err);
      setResumes((prev) =>
        prev.map((item) =>
          item._id === resumeId ? { ...item, group_id: previousGroupId } : item
        )
      );
      toast.error("Failed to update resume group");
    }
  };

  // Fetch team categories
  const fetchTeamCategories = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/categories`);
      setTeamCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch team categories:', error);
      // Fallback to default categories if API fails
      setTeamCategories(['general']);
    }
  };

  // Add function to fetch team bidders
  const fetchTeamBidders = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/teams/my-team`);
      // Filter bidders who have the current user as their master
      const bidders = response.data.filter(member => 
        member.role === 'bidder' && 
        member.master && 
        (member.master._id == user._id || member.master == user._id)
      );
      setTeamBidders(bidders);
    } catch (error) {
      console.error('Error fetching team bidders:', error);
      toast.error('Failed to fetch team bidders');
    }
  };

  // Add function to fetch available Google tokens
  const fetchAvailableGoogleTokens = async () => {
    setLoadingGoogleTokens(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/google-tokens`
      );
      setAvailableGoogleTokens(response.data.tokens || []);
    } catch (error) {
      console.error('Error fetching Google tokens:', error);
      toast.error('Failed to fetch available Google tokens');
    } finally {
      setLoadingGoogleTokens(false);
    }
  };

  // Add function to handle bidder visibility changes
  const handleBidderVisibilityChange = async (bidderId) => {
    if (!selectedResume) return;

    const previousBidderId = visibleToBidder;
    const normalizedBidderId = bidderId || "";
    setVisibleToBidder(normalizedBidderId);

    try {
      setSavingBidderVisibility(true);

      await axios.put(
        `${process.env.REACT_APP_API_URL}/resumes/${selectedResume._id}/bidder-visibility`,
        { visibleToBidder: normalizedBidderId || null }
      );

      const visibleToBidders = normalizedBidderId ? [normalizedBidderId] : [];
      const updatedResumes = resumes.map((resume) =>
        resume._id === selectedResume._id
          ? {
              ...resume,
              showToBidder: Boolean(normalizedBidderId),
              visibleToBidders,
            }
          : resume
      );
      setResumes(updatedResumes);

      const bidder = teamBidders.find((b) => b._id === normalizedBidderId);
      toast.success(
        normalizedBidderId
          ? `Resume shared with ${bidder?.name || "selected bidder"}`
          : "Resume hidden from bidders"
      );
    } catch (error) {
      console.error("Error updating bidder visibility:", error);
      setVisibleToBidder(previousBidderId);
      toast.error("Failed to update bidder visibility");
    } finally {
      setSavingBidderVisibility(false);
    }
  };

  useEffect(() => {
    fetchResumes();
    fetchResumeGroups();
    fetchTeamCategories();
    fetchTeamBidders();
    fetchAvailableGoogleTokens();
    loadCollapsedGroups();
  }, []);

  // Add a new useEffect to fetch scheduled emails when selected resume changes
  useEffect(() => {
    if (selectedResume) {
      setVisibleToBidder(getVisibleBidderId(selectedResume));
      fetchScheduledEmailApplications();
    }
  }, [selectedResume]);

  // Add useEffect to update Gmail and auto email settings when selected resume changes
  useEffect(() => {
    if (selectedResume) {
      // Update auto email settings
      setAutoEmailEnabled(!!selectedResume.auto_email_application);
      setCoverLetterTitle(selectedResume.cover_letter_title || "");
      setCoverLetterContent(selectedResume.cover_letter_content || "");
      setAutoCustomizeResume(resolveAutoCustomizeResume(selectedResume));
      
      // Update Gmail connection status
      setGmailConnected(!!selectedResume.gmail_status?.connected);
      setGmailEmail(selectedResume.gmail_status?.email || selectedResume.gmail_email || "");
      setGmailTokenExpiryDate(
        selectedResume.gmail_status?.tokenExpiryDate 
          ? new Date(selectedResume.gmail_status.tokenExpiryDate) 
          : null
      );
      
      // Update selected Google token to match the resume's stored token
      setSelectedGoogleToken(
        selectedResume.gmail_status?.tokenIndex !== undefined 
          ? selectedResume.gmail_status.tokenIndex 
          : 0
      );
      
      // Update Gmail cleanup settings
      setGmailCleanup(!!selectedResume.gmail_cleanup_status?.gmail_auto_cleanup);
      
      // Update filters
      if (selectedResume.auto_email_filters) {
        setLocationFilter(selectedResume.auto_email_filters.location_filter || []);
        setCategoryFilter(selectedResume.auto_email_filters.category_filter || []);
        setBehaviorFilter(selectedResume.auto_email_filters.behavior_filter || ["all"]);
        setQueryDateLimit(selectedResume.auto_email_filters.query_date_limit || [-1]);
      }
    }
  }, [selectedResume]);

  useEffect(() => {
    if (selectedResume?.gmail_status?.connected) {
      fetchGmailForwardingStatus();
    }
  }, [selectedResume?._id, selectedResume?.gmail_status?.connected]);

  const applyLocalForwardingStatus = (status) => {
    const updatedResumes = [...resumes];
    if (updatedResumes[selectedResumeIndex]) {
      updatedResumes[selectedResumeIndex] = {
        ...updatedResumes[selectedResumeIndex],
        gmail_forwarding_status: {
          forward_to: status.forward_to || null,
          enabled: !!status.enabled,
          verification_status: status.verification_status || null,
          can_configure: status.can_configure !== false,
        },
      };
      setResumes(updatedResumes);
    }
  };

  const fetchGmailForwardingStatus = async () => {
    if (!selectedResume?.gmail_status?.connected) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/resumes/${selectedResume._id}/gmail-forwarding`
      );
      applyLocalForwardingStatus(response.data);
    } catch (error) {
      console.error("Error fetching Gmail forwarding status:", error);
    }
  };

  const handleSave = async () => {
    if (!editedResume) return;

    setSavingResume(true);
    try {
      const formData = new FormData();
      formData.append("owner", editedResume.owner);
      formData.append(
        "content",
        JSON.stringify({
          personal_info: editedResume.personal_info,
          profile: editedResume.profile,
          education: editedResume.education,
          experience: editedResume.experience,
          summarized_experience: editedResume.summarized_experience,
          skillset: editedResume.skillset,
          additional_info: editedResume.additional_info,
        })
      );

      if (fileToUpload) {
        formData.append("resume", fileToUpload);
      }

      // If it's a new resume (has temporary ID)
      if (editedResume._id.startsWith("temp-")) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/resumes`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        // Update the resume with the real ID from the server
        editedResume._id = response.data._id;
        editedResume.path = response.data.path;

        if (coverLetterTitle.trim() || coverLetterContent.trim()) {
          await axios.put(
            `${process.env.REACT_APP_API_URL}/resumes/${editedResume._id}/auto-email-settings`,
            {
              cover_letter_title: coverLetterTitle,
              cover_letter_content: coverLetterContent,
            },
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );
          editedResume.cover_letter_title = coverLetterTitle;
          editedResume.cover_letter_content = coverLetterContent;
        }
      } else {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/resumes/${editedResume._id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      const updatedResumes = [...resumes];
      updatedResumes[selectedResumeIndex] = editedResume;
      setResumes(updatedResumes);
      setIsEditing(false);
      setFileToUpload(null);
      setError("");
      await fetchResumes();
    } catch (err) {
      setError("Failed to save resume");
    } finally {
      setSavingResume(false);
    }
  };

  const handleEdit = () => {
    setEditedResume(JSON.parse(JSON.stringify(resumes[selectedResumeIndex])));
    setIsEditing(true);
  };

  const handleFieldChange = (section, field, value) => {
    if (!editedResume) return;

    if (field === "") {
      // Direct update for non-nested fields like 'profile'
      setEditedResume({
        ...editedResume,
        [section]: value,
      });
    } else {
      // Nested update for fields like personal_info.name
      setEditedResume({
        ...editedResume,
        [section]: {
          ...editedResume[section],
          [field]: value,
        },
      });
    }
  };

  const handleExperienceChange = (id, field, value) => {
    if (!editedResume) return;

    setEditedResume({
      ...editedResume,
      experience: editedResume.experience.map((exp) =>
        exp.id === id ? { ...exp, [field]: value } : exp
      ),
    });
  };

  const handleEducationChange = (id, field, value) => {
    if (!editedResume) return;

    setEditedResume({
      ...editedResume,
      education: editedResume.education.map((edu) =>
        edu.id === id ? { ...edu, [field]: value } : edu
      ),
    });
  };

  const handleSkillChange = (id, field, value) => {
    if (!editedResume) return;

    setEditedResume({
      ...editedResume,
      skillset: editedResume.skillset.map((skill) =>
        skill.id === id ? { ...skill, [field]: value } : skill
      ),
    });
  };

  const handleAddSkill = () => {
    if (!editedResume) return;

    const newSkill = {
      id: editedResume.skillset.length + 1,
      category: "",
      skills: "",
    };

    setEditedResume({
      ...editedResume,
      skillset: [...editedResume.skillset, newSkill],
    });
  };

  const createNewResume = async () => {
    const emptyResume = {
      _id: `temp-${Date.now()}`, // Temporary ID for local state
      owner: user._id,
      nickname: null,
      personal_info: {
        name: "",
        title: "",
        email: "",
        phone_number: "",
        location: "",
        linkedin: "",
      },
      profile: "",
      education: [
        {
          id: 1,
          institution: "",
          degree: "",
          year: "",
        },
      ],
      experience: [],
      summarized_experience: [],
      skillset: [],
      additional_info: "",
      gmail_credentials_setup: false,
      gmail_email: "",
      is_enabled: true,
    };

    const updatedResumes = sortResumesByEnabled([...resumes, emptyResume]);
    setResumes(updatedResumes);
    setSelectedResumeIndex(updatedResumes.findIndex((resume) => resume._id === emptyResume._id));
    setIsEditing(true);
    setEditedResume(emptyResume);
  };

  const handleDeleteClick = (resumeId, index) => {
    const resume = resumes[index];
    setResumeToDelete({ id: resumeId, index, resume });
    setMigrateGmailOnDelete(!!resume?.gmail_status?.connected);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!resumeToDelete) return;

    try {
      const resume = resumeToDelete.resume;
      const shouldMigrate =
        migrateGmailOnDelete && resume?.gmail_status?.connected;

      if (shouldMigrate) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/gmail-mailboxes/from-resume/${resumeToDelete.id}`,
          {
            label:
              resume.gmail_status?.email ||
              resume.gmail_email ||
              resume.personal_info?.email ||
              resume.nickname ||
              "Migrated mailbox",
          }
        );
        window.dispatchEvent(new Event("gmail-status-refreshed"));
      }

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/resumes/${resumeToDelete.id}`
      );
      const updatedResumes = resumes.filter(
        (_, i) => i !== resumeToDelete.index
      );
      setResumes(updatedResumes);

      if (resumeToDelete.index === selectedResumeIndex) {
        setSelectedResumeIndex(Math.max(0, updatedResumes.length - 1));
      } else if (resumeToDelete.index < selectedResumeIndex) {
        setSelectedResumeIndex(selectedResumeIndex - 1);
      }

      if (shouldMigrate) {
        toast.success("Gmail moved to standalone cleanup and resume deleted");
      }
    } catch (err) {
      setError("Failed to delete resume");
    } finally {
      setDeleteDialogOpen(false);
      setResumeToDelete(null);
    }
  };

  const handleAddExperience = () => {
    if (!editedResume) return;

    const newExperience = {
      id: editedResume.experience.length + 1,
      company: "",
      position: "",
      duration: "",
      description: "",
    };

    setEditedResume({
      ...editedResume,
      experience: [...editedResume.experience, newExperience],
    });
  };

  const handleRemoveSkill = (id) => {
    if (!editedResume) return;

    setEditedResume({
      ...editedResume,
      skillset: editedResume.skillset.filter((skill) => skill.id !== id),
    });
  };

  const handleRemoveExperience = (id) => {
    if (!editedResume) return;

    setEditedResume({
      ...editedResume,
      experience: editedResume.experience.filter((exp) => exp.id !== id),
    });
  };

  const handleCancel = () => {
    if (editedResume?._id.startsWith("temp-")) {
      // Remove the temporary resume from the list
      const updatedResumes = resumes.filter(
        (_, index) => index !== selectedResumeIndex
      );
      setResumes(updatedResumes);
      setSelectedResumeIndex(Math.max(0, updatedResumes.length - 1));
    }
    setIsEditing(false);
    setEditedResume(null);
  };

  const handleVersionsChange = (versions) => {
    const resumeId = selectedResume?._id;
    if (!resumeId) return;

    setResumes((prev) =>
      prev.map((resume) =>
        resume._id === resumeId ? { ...resume, versions } : resume
      )
    );
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileToUpload(file);
      setIsAnalyzing(true);

      try {
        // Convert file to array buffer
        const arrayBuffer = await file.arrayBuffer();

        // Load the PDF document
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        let fullText = "";

        // Extract text from each page
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map((item) => item.str).join(" ");
          fullText += pageText + "\n";
        }

        // Send the extracted text to backend for analysis
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/analyze`,
          { type: "extractResumeInfo", data: { resumeText: fullText } },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        // Update the edited resume with the analyzed data
        if (response.data) {
          const analyzedResume = response.data;
          const isNewResume = editedResume?._id?.startsWith("temp-");
          const updatedResume = {
            ...editedResume,
            personal_info: {
              name:
                analyzedResume.personal_info.name || editedResume.personal_info.name,
              title:
                analyzedResume.personal_info.title || editedResume.personal_info.title,
              email:
                analyzedResume.personal_info.email || editedResume.personal_info.email,
              phone_number:
                analyzedResume.personal_info.phone_number ||
                editedResume.personal_info.phone_number,
              location:
                analyzedResume.personal_info.location ||
                editedResume.personal_info.location,
              linkedin:
                analyzedResume.personal_info.linkedin ||
                editedResume.personal_info.linkedin,
            },
            profile: analyzedResume.profile || editedResume.profile,
            experience: analyzedResume.experience.map((exp, index) => ({
              id: index + 1,
              company: exp.company,
              position: exp.position,
              duration: exp.duration,
              description: exp.description,
            })),
            summarized_experience: analyzedResume.summarized_experience.map(
              (exp, index) => ({
                id: index + 1,
                company: exp.company,
                position: exp.position,
                duration: exp.duration,
                description: exp.description,
              })
            ),
            education: analyzedResume.education.map((edu, index) => ({
              id: index + 1,
              institution: edu.institution,
              degree: edu.degree,
              year: edu.year,
            })),
            skillset: analyzedResume.skillset.map((skill, index) => ({
              id: index + 1,
              category: skill.category,
              skills: skill.skills,
            })),
            additional_info: editedResume.additional_info || "",
          };

          setEditedResume(updatedResume);

          if (isNewResume && updatedResume.personal_info?.name) {
            try {
              const coverLetterResponse = await axios.post(
                `${process.env.REACT_APP_API_URL}/analyze`,
                {
                  type: "generateResumeSettingsCoverLetter",
                  data: {
                    resumeData: buildResumeContentPayload(updatedResume),
                  },
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                  },
                }
              );

              if (coverLetterResponse.data?.title) {
                setCoverLetterTitle(coverLetterResponse.data.title);
              }
              if (coverLetterResponse.data?.coverLetter) {
                setCoverLetterContent(coverLetterResponse.data.coverLetter);
              }
            } catch (coverLetterError) {
              console.warn("Cover letter generation failed:", coverLetterError);
              toast.warn(
                "Resume parsed, but cover letter could not be generated. You can generate it manually."
              );
            }
          }
        }
      } catch (error) {
        console.error("Error processing PDF:", error);
        console.error("Error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setError("Failed to process resume file");
      } finally {
        setIsAnalyzing(false);
      }
    }
  };

  const handleAutoEmailToggle = async (checked) => {
    if (!selectedResume) return;

    setAutoEmailEnabled(checked);
    setSavingAutoEmail(true);

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/resumes/${selectedResume._id}/auto-email-application`,
        { auto_email_application: checked },
        { headers: { "Content-Type": "application/json" } }
      );

      // Update the local resume data
      const updatedResumes = [...resumes];
      updatedResumes[selectedResumeIndex] = {
        ...updatedResumes[selectedResumeIndex],
        auto_email_application: checked,
        cover_letter_title: coverLetterTitle,
        cover_letter_content: coverLetterContent,
        auto_email_filters: {
          location_filter: locationFilter,
          category_filter: categoryFilter,
          behavior_filter: behaviorFilter,
          confidence_range: [0.3, 1],
          query_date_limit: queryDateLimit,
        }
      };
      setResumes(updatedResumes);

      setError("");
      toast.success(
        checked
          ? "Automatic email applications enabled successfully!"
          : "Automatic email applications disabled successfully!"
      );
    } catch (err) {
      setError("Failed to update automatic email application settings");
      toast.error("Failed to update automatic email application settings");
      setAutoEmailEnabled(!checked); // Revert the change
    } finally {
      setSavingAutoEmail(false); // Moved to finally block
    }
  };

  const handleSaveAutoEmailSettings = async () => {
    if (!selectedResume) return;

    setSavingAutoEmailSettings(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/resumes/${selectedResume._id}/auto-email-settings`,
        {
          cover_letter_title: coverLetterTitle,
          cover_letter_content: coverLetterContent,
          location_filter: locationFilter,
          category_filter: categoryFilter,
          behavior_filter: behaviorFilter,
          confidence_range: [0.3, 1],
          query_date_limit: queryDateLimit,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Update the local resume data
      const updatedResumes = [...resumes];
      updatedResumes[selectedResumeIndex] = {
        ...updatedResumes[selectedResumeIndex],
        cover_letter_title: coverLetterTitle,
        cover_letter_content: coverLetterContent,
        auto_email_filters: {
          location_filter: locationFilter,
          category_filter: categoryFilter,
          behavior_filter: behaviorFilter,
          confidence_range: [0.3, 1],
          query_date_limit: queryDateLimit,
        }
      };
      setResumes(updatedResumes);

      setError("");
      toast.success("Settings saved successfully!");
    } catch (err) {
      setError("Failed to save automatic email application settings");
      toast.error("Failed to save automatic email application settings");
    } finally {
      setSavingAutoEmailSettings(false);
    }
  };

  const handleGenerateCoverLetter = async () => {
    if (!selectedResume) return;

    setGeneratingCoverLetter(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/resumes/${selectedResume._id}/generate-cover-letter`
      );

      if (response.data?.title) {
        setCoverLetterTitle(response.data.title);
      }
      if (response.data?.coverLetter) {
        setCoverLetterContent(response.data.coverLetter);
      }

      toast.success("Cover letter generated. Review it and save your settings when ready.");
    } catch (err) {
      const message = err.response?.data?.message || "Failed to generate cover letter";
      toast.error(message);
    } finally {
      setGeneratingCoverLetter(false);
    }
  };

  const handleAutoCustomizeToggle = async (checked) => {
    if (!selectedResume) return;

    const previous = autoCustomizeResume;
    setAutoCustomizeResume(checked);
    setSavingAutoCustomizeResume(true);

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/resumes/${selectedResume._id}/auto-customize-resume`,
        { auto_customize_resume: checked },
        { headers: { "Content-Type": "application/json" } }
      );

      const updatedResumes = [...resumes];
      updatedResumes[selectedResumeIndex] = {
        ...updatedResumes[selectedResumeIndex],
        auto_customize_resume: checked,
      };
      setResumes(updatedResumes);
      toast.success(
        checked
          ? "Auto customize resume enabled"
          : "Auto customize resume disabled"
      );
    } catch (err) {
      setAutoCustomizeResume(previous);
      toast.error("Failed to update auto customize resume setting");
    } finally {
      setSavingAutoCustomizeResume(false);
    }
  };

  const handleGenerateAuthUrl = async () => {
    if (!selectedResume) return;

    setGeneratingAuthUrl(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/gmail/url/${selectedResume._id}?tokenIndex=${selectedGoogleToken}`
      );

      setAuthUrl(response.data.authUrl);
      setShowAuthDialog(true);
    } catch (error) {
      console.error("Error generating auth URL:", error);
      toast.error("Failed to generate Gmail authorization URL");
    } finally {
      setGeneratingAuthUrl(false);
    }
  };

  const handleRemoveGmailCredentials = async () => {
    if (!selectedResume) return;
    if (
      !window.confirm(
        'Are you sure you want to remove Gmail credentials from this resume?'
      )
    ) {
      return;
    }

    setRemovingGmail(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/resumes/${selectedResume._id}/gmail-credentials`
      );

      // Refresh the resume data from server to get updated Gmail status
      await fetchResumes();

      toast.success("Gmail credentials removed successfully");
      // Notify Navigation to refresh the Gmail expiry counter
      window.dispatchEvent(new Event('gmail-status-refreshed'));
    } catch (error) {
      console.error("Error removing Gmail credentials:", error);
      toast.error("Failed to remove Gmail credentials");
    } finally {
      setRemovingGmail(false);
    }
  };

  const handleRefreshGmailStatus = async () => {
    if (!selectedResume) return;

    try {
      await fetchResumes();
      toast.success("Gmail status refreshed");
      // Notify Navigation to refresh the Gmail expiry counter
      window.dispatchEvent(new Event('gmail-status-refreshed'));
    } catch (error) {
      console.error("Error refreshing Gmail status:", error);
      toast.error("Failed to refresh Gmail status");
    }
  };

  // Add a function to check if token is about to expire
  const isTokenNearExpiry = () => {
    if (!gmailTokenExpiryDate) return false;

    const now = new Date();
    const oneDayInMs = 24 * 60 * 60 * 1000;
    return gmailTokenExpiryDate - now <= oneDayInMs;
  };

  const fetchScheduledEmailApplications = async () => {
    if (!selectedResume) return;

    if (selectedResume._id?.startsWith("temp-")) {
      setScheduledEmails({
        queueItems: [],
        nextSchedule: null
      });
      return;
    }

    setLoadingScheduledEmails(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/applications/scheduled/${selectedResume._id}`
      );
      setScheduledEmails(response.data);
    } catch (error) {
      console.error("Error fetching scheduled emails:", error);
    } finally {
      setLoadingScheduledEmails(false);
    }
  };

  const handleSaveNickname = async (resumeId) => {
    setSavingNickname(true);
    try {
      const trimmedNickname = nicknameInput.trim();
      const nextNickname = trimmedNickname || null;

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/resumes/${resumeId}/nickname`,
        { nickname: nextNickname },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const savedNickname = response.data?.nickname ?? nextNickname;

      // Update local state
      const updatedResumes = resumes.map(resume => 
        resume._id === resumeId 
          ? { ...resume, nickname: savedNickname }
          : resume
      );
      setResumes(updatedResumes);

      setEditingNicknameId(null);
      setNicknameInput("");
      toast.success(
        savedNickname ? "Nickname updated successfully!" : "Nickname removed."
      );
    } catch (err) {
      console.error('Failed to save nickname:', err);
      toast.error("Failed to save nickname. Please try again.");
    } finally {
      setSavingNickname(false);
    }
  };

  const handleCancelNickname = () => {
    setEditingNicknameId(null);
    setNicknameInput("");
  };

  const handleEditNickname = (resume) => {
    setEditingNicknameId(resume._id);
    setNicknameInput(resume.nickname || "");
  };

  const handleStartEditNickname = (resume) => {
    setEditingNicknameId(resume._id);
    setNicknameInput(resume.nickname || "");
  };

  const handleGmailCleanupToggle = async (checked) => {
    if (!selectedResume) return;

    setSavingGmailCleanup(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/resumes/${selectedResume._id}/gmail-cleanup`,
        { enabled: checked },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Update the local resume data
      const updatedResumes = [...resumes];
      updatedResumes[selectedResumeIndex] = {
        ...updatedResumes[selectedResumeIndex],
        gmail_cleanup_status: {
          ...updatedResumes[selectedResumeIndex].gmail_cleanup_status,
          gmail_auto_cleanup: checked,
        },
      };
      setResumes(updatedResumes);

      setGmailCleanup(checked);
      setCleanupError("");
    } catch (err) {
      setCleanupError("Failed to update Gmail cleanup setting");
      // Revert the toggle if the API call failed
      setGmailCleanup(!checked);
    } finally {
      setSavingGmailCleanup(false);
    }
  };

  const handleResumeEnabledToggle = async () => {
    if (!selectedResume) return;

    setSavingResumeEnabled(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/resumes/${selectedResume._id}/toggle-enabled`
      );

      const nextEnabledState = response.data?.is_enabled !== false;
      const updatedResumes = sortResumesByEnabled(
        resumes.map((resume) => {
          if (resume._id !== selectedResume._id) return resume;

          return {
            ...resume,
            is_enabled: nextEnabledState,
          };
        })
      );

      setResumes(updatedResumes);
      const newSelectedIndex = updatedResumes.findIndex((resume) => resume._id === selectedResume._id);
      setSelectedResumeIndex(newSelectedIndex >= 0 ? newSelectedIndex : 0);

      toast.success(`Resume ${nextEnabledState ? "enabled" : "disabled"} successfully`);
    } catch (error) {
      toast.error("Failed to update resume status");
    } finally {
      setSavingResumeEnabled(false);
    }
  };

  const handleLocationFilterChange = (location) => {
    let newLocationFilter;
    
    if (location === "all") {
      // If "Anywhere" is selected, deselect all other locations
      newLocationFilter = ["all"];
    } else {
      // If a specific location is selected, remove "Anywhere" and add the location
      if (locationFilter.includes("all")) {
        // Remove "all" and add the specific location
        newLocationFilter = [location];
      } else if (locationFilter.includes(location)) {
        // Remove the specific location
        newLocationFilter = locationFilter.filter((loc) => loc !== location);
        // If no locations left, default to "all"
        if (newLocationFilter.length === 0) {
          newLocationFilter = ["all"];
        }
      } else {
        // Add the specific location (removing "all" if it exists)
        newLocationFilter = locationFilter.filter((loc) => loc !== "all");
        newLocationFilter.push(location);
      }
    }
    
    setLocationFilter(newLocationFilter);
  };

  const handleCategoryFilterChange = (category) => {
    let newCategoryFilter;
    if (category === "all") {
      newCategoryFilter = ["all"];
    } else if (categoryFilter.includes("all")) {
      newCategoryFilter = [category];
    } else if (categoryFilter.includes(category)) {
      newCategoryFilter = categoryFilter.filter((c) => c !== category);
      if (newCategoryFilter.length === 0) {
        newCategoryFilter = ["all"];
      }
    } else {
      newCategoryFilter = [...categoryFilter, category];
    }

    setCategoryFilter(newCategoryFilter);
  };

  const handleQueryDateLimitChange = (event) => {
    const value = event.target.value;
    setQueryDateLimit(Array.isArray(value) ? value : [value]);
  };

  const renderResumeListItem = (resume, { nested = false } = {}) => {
    const index = resumes.findIndex((item) => item._id === resume._id);
    const isSelected = index === selectedResumeIndex;
    const isNearExpiry = resume.gmail_status?.isNearExpiry || false;
    const isEditingNickname = editingNicknameId === resume._id;
    const displayName = resume.nickname || resume.personal_info?.name || "Untitled Resume";
    const statusIcons = [
      resume.auto_email_application && {
        key: "email",
        color: "#4caf50",
        title: "Auto Email Enabled",
        icon: <EmailIcon sx={{ fontSize: "12px", color: "white" }} />,
      },
      resume.gmail_status?.connected && {
        key: "gmail",
        color: "#2196f3",
        title: "Gmail Connected",
        icon: <LinkIcon sx={{ fontSize: "12px", color: "white" }} />,
      },
      resume.gmail_cleanup_status?.gmail_auto_cleanup && {
        key: "gmail-cleanup",
        color: "#ff5722",
        title: "Auto Gmail Cleanup Enabled",
        icon: <DeleteSweepIcon sx={{ fontSize: "12px", color: "white" }} />,
      },
      resolveAutoCustomizeResume(resume) && {
        key: "auto-customize",
        color: "#9c27b0",
        title: "Auto Customize Resume Enabled",
        icon: <AutoAwesomeIcon sx={{ fontSize: "12px", color: "white" }} />,
      },
      isNearExpiry && {
        key: "expiry",
        color: "#f44336",
        title: "Gmail token expiring soon",
        icon: (
          <Typography
            component="span"
            sx={{ fontSize: "12px", color: "white", fontWeight: "bold", lineHeight: 1 }}
          >
            !
          </Typography>
        ),
      },
    ].filter(Boolean);

    return (
      <ListItemButton
        key={resume._id}
        selected={isSelected}
        onClick={() => setSelectedResumeIndex(index)}
        sx={(theme) => ({
          flexDirection: "column",
          alignItems: "stretch",
          textAlign: "center",
          p: 1.25,
          mb: 1,
          ml: nested ? 0.75 : 0,
          borderRadius: 2,
          border: "1px solid",
          borderColor: isSelected ? "primary.main" : "divider",
          bgcolor: isSelected
            ? theme.palette.mode === "dark"
              ? "rgba(0, 200, 255, 0.1)"
              : "action.selected"
            : theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.03)"
              : "background.paper",
          boxShadow: isSelected ? 1 : 0,
          opacity: resume.is_enabled === false ? 0.55 : draggingResumeId === resume._id ? 0.45 : 1,
          filter: resume.is_enabled === false ? "grayscale(100%)" : "none",
          transition: "border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease",
          "&:hover": {
            borderColor: isSelected ? "primary.main" : "text.disabled",
          },
          "&.Mui-selected": {
            bgcolor: theme.palette.mode === "dark"
              ? "rgba(0, 200, 255, 0.12)"
              : "action.selected",
            "&:hover": {
              bgcolor: theme.palette.mode === "dark"
                ? "rgba(0, 200, 255, 0.16)"
                : "action.selected",
            },
          },
        })}
      >
        {isEditingNickname ? (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%" }}
            onClick={(event) => event.stopPropagation()}
          >
            <TextField
              size="small"
              placeholder="Nickname (leave empty to remove)"
              value={nicknameInput}
              onChange={(e) => setNicknameInput(e.target.value)}
              sx={{ flex: 1 }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSaveNickname(resume._id);
                }
              }}
            />
            <IconButton
              size="small"
              onClick={() => handleSaveNickname(resume._id)}
              disabled={savingNickname}
              color="primary"
            >
              {savingNickname ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={handleCancelNickname}>
              <CancelIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "28px 1fr 28px",
                alignItems: "center",
                width: "100%",
                columnGap: 0.5,
              }}
            >
              <Box
                draggable
                onDragStart={(event) => handleResumeDragStart(event, resume._id)}
                onDragEnd={handleResumeDragEnd}
                onClick={(event) => event.stopPropagation()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "grab",
                  color: "text.secondary",
                  "&:active": { cursor: "grabbing" },
                }}
                title="Drag to move into a group"
              >
                <DragIndicatorIcon fontSize="small" />
              </Box>

              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  textAlign: "center",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  color: "text.primary",
                  px: 0.25,
                }}
              >
                {displayName}
              </Typography>

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartEditNickname(resume);
                }}
                color="primary"
                title="Edit nickname"
                sx={{ p: 0.5 }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {(resume.is_enabled === false || statusIcons.length > 0 || isNearExpiry || (getVisibleBidderId(resume) && !isEditingNickname)) && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 0.75,
                  mt: 1,
                  minHeight: 22,
                }}
              >
                {resume.is_enabled === false && (
                  <Chip size="small" label="Disabled" sx={{ height: 22, fontSize: "0.7rem" }} />
                )}
                {statusIcons.map((status) => (
                  <Box
                    key={status.key}
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      backgroundColor: status.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                    title={status.title}
                  >
                    {status.icon}
                  </Box>
                ))}
                {getVisibleBidderId(resume) && !isEditingNickname && (() => {
                  const bidderId = getVisibleBidderId(resume);
                  const bidder = teamBidders.find((b) => b._id === bidderId);
                  return (
                    <Chip
                      key={bidderId}
                      label={bidder ? bidder.name : bidderId}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{
                        fontSize: "0.68rem",
                        height: 22,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  );
                })()}
              </Box>
            )}
          </>
        )}
      </ListItemButton>
    );
  };

  const renderGroupSection = (group) => {
    const groupResumes = getResumesForGroup(group._id);
    const collapsed = isGroupCollapsed(group._id);

    return (
      <Box key={group._id} sx={{ mb: 1.5 }}>
        <Box
          onDragOver={(event) => handleGroupDragOver(event, group._id)}
          onDrop={(event) => handleGroupDrop(event, group._id)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            py: 0.75,
            mb: 0.75,
            borderRadius: 1.5,
            backgroundColor: "action.hover",
            transition: "background-color 0.15s ease",
            ...getGroupDropZoneSx(group._id),
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, cursor: "pointer", minWidth: 0 }}
            onClick={() => toggleGroupCollapsed(group._id)}
          >
            <IconButton size="small" sx={{ p: 0.5 }}>
              {collapsed ? <KeyboardArrowRightIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }} noWrap>
              {group.name}
            </Typography>
            <Chip size="small" label={groupResumes.length} sx={{ height: 20, fontSize: "0.7rem", flexShrink: 0 }} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditGroup(group);
              }}
              title="Edit group"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteGroupClick(group);
              }}
              title="Delete group"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Collapse in={!collapsed}>
          <List
            disablePadding
            onDragOver={(event) => handleGroupDragOver(event, group._id)}
            onDrop={(event) => handleGroupDrop(event, group._id)}
            sx={{
              minHeight: groupResumes.length === 0 ? 40 : undefined,
              px: 0.5,
              borderRadius: 1,
              transition: "background-color 0.15s ease",
              ...getGroupDropZoneSx(group._id),
            }}
          >
            {groupResumes.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ py: 1, display: "block", textAlign: "center" }}>
                Drop resumes here
              </Typography>
            ) : (
              groupResumes.map((resume) => renderResumeListItem(resume, { nested: true }))
            )}
          </List>
        </Collapse>
      </Box>
    );
  };

  const ungroupedResumes = getResumesForGroup(null);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Remove the duplicate selectedResume definition
  const displayedResume = isEditing ? editedResume : selectedResume;

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto" }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          pt: 1,
          pb: 0,
          mb: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Tabs
            value={pageTab}
            onChange={(_, value) => setPageTab(value)}
            sx={{ minHeight: 48 }}
          >
            <Tab label="Profiles" />
            <Tab
              label="Gmail cleanup"
              icon={<DeleteSweepIcon />}
              iconPosition="start"
            />
          </Tabs>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DoNotTouchIcon />}
            onClick={() => setSenderBlacklistDrawerOpen(true)}
            sx={{ mr: 1, mb: 0.5 }}
          >
            Sender blacklists
          </Button>
        </Box>
      </Box>

      <GmailSenderBlacklistDrawer
        open={senderBlacklistDrawerOpen}
        onClose={() => setSenderBlacklistDrawerOpen(false)}
      />

      {pageTab === 0 && (
    <Box sx={{ display: "flex", gap: 2, mt: 2, alignItems: "flex-start" }}>
      {/* Left sidebar with resume list */}
      <Paper
        sx={{
          width: 288,
          flexShrink: 0,
          p: 1.5,
          position: "sticky",
          top: STICKY_TABS_HEIGHT,
          alignSelf: "flex-start",
          maxHeight: `calc(100vh - ${STICKY_TABS_HEIGHT + 24}px)`,
          overflowY: "auto",
          color: "text.primary",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1.5}
          px={0.5}
        >
          <Typography variant="h6" sx={{ color: "text.primary" }}>Resumes</Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Button variant="outlined" size="small" onClick={handleOpenCreateGroup} title="Add group">
              <CreateNewFolderOutlinedIcon fontSize="small" />
            </Button>
            <Button variant="contained" size="small" onClick={createNewResume}>
              <AddIcon />
              Add
            </Button>
          </Box>
        </Box>
        {resumes.length === 0 ? (
          <Typography align="center" sx={{ mt: 2 }}>
            No resumes found. Click 'Add' to create one.
          </Typography>
        ) : (
          <List disablePadding>
            {resumeGroups.map((group) => renderGroupSection(group))}

            {resumeGroups.length > 0 ? (
              <Box sx={{ mb: 1.5 }}>
                <Box
                  onDragOver={(event) => handleGroupDragOver(event, null)}
                  onDrop={(event) => handleGroupDrop(event, null)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.75,
                    mb: 0.75,
                    borderRadius: 1.5,
                    backgroundColor: "action.hover",
                    cursor: "pointer",
                    transition: "background-color 0.15s ease",
                    ...getGroupDropZoneSx(null),
                  }}
                  onClick={() => toggleGroupCollapsed(null)}
                >
                  <IconButton size="small" sx={{ p: 0.5 }}>
                    {isGroupCollapsed(null) ? (
                      <KeyboardArrowRightIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowDownIcon fontSize="small" />
                    )}
                  </IconButton>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
                    Ungrouped
                  </Typography>
                  <Chip size="small" label={ungroupedResumes.length} sx={{ height: 20, fontSize: "0.7rem" }} />
                </Box>
                <Collapse in={!isGroupCollapsed(null)}>
                  <List
                    disablePadding
                    onDragOver={(event) => handleGroupDragOver(event, null)}
                    onDrop={(event) => handleGroupDrop(event, null)}
                    sx={{
                      minHeight: ungroupedResumes.length === 0 ? 40 : undefined,
                      px: 0.5,
                      borderRadius: 1,
                      transition: "background-color 0.15s ease",
                      ...getGroupDropZoneSx(null),
                    }}
                  >
                    {ungroupedResumes.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ py: 1, display: "block", textAlign: "center" }}>
                        Drop resumes here to ungroup
                      </Typography>
                    ) : (
                      ungroupedResumes.map((resume) => renderResumeListItem(resume))
                    )}
                  </List>
                </Collapse>
              </Box>
            ) : (
              <Box sx={{ px: 0.5 }}>
                {ungroupedResumes.map((resume) => renderResumeListItem(resume))}
              </Box>
            )}
          </List>
        )}
      </Paper>

      {/* Right side with resume details */}
      {displayedResume && resumes.length > 0 && (
        <Paper sx={{ flex: 1, minWidth: 0, p: 4 }}>
          <Box
            display="flex"
            justifyContent="flex-end"
            mb={2}
            sx={{
              position: "sticky",
              top: STICKY_TABS_HEIGHT,
              zIndex: 10,
              bgcolor: "background.paper",
              py: 1,
              mx: -1,
              px: 1,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            {!isEditing ? (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isSelectedResumeEnabled}
                      onChange={handleResumeEnabledToggle}
                      color="primary"
                      disabled={savingResumeEnabled}
                    />
                  }
                  label={isSelectedResumeEnabled ? "On" : "Off"}
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => setCustomizedResumesOpen(true)}
                  sx={{ mr: 1 }}
                >
                  Customized Resumes
                </Button>
                {selectedResume?.path && (
                  <Button
                    variant="outlined"
                    onClick={() =>
                      window.open(
                        `${process.env.REACT_APP_API_URL}/resumefiles/${selectedResume.path}`,
                        "_blank"
                      )
                    }
                    sx={{ mr: 1 }}
                  >
                    View Resume File
                  </Button>
                )}
                <Button
                  startIcon={<EditIcon />}
                  variant="contained"
                  onClick={handleEdit}
                  sx={{ mr: 1 }}
                >
                  Edit
                </Button>
                <Button
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  color="error"
                  onClick={() =>
                    handleDeleteClick(selectedResume._id, selectedResumeIndex)
                  }
                >
                  Delete
                </Button>
              </>
            ) : (
              <>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                  id="resume-file-input"
                />
                <label htmlFor="resume-file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={
                      isAnalyzing ? <CircularProgress size={20} /> : <AddIcon />
                    }
                    sx={{ mr: 1 }}
                    disabled={isAnalyzing}
                  >
                    {isAnalyzing
                      ? "Analyzing Resume..."
                      : fileToUpload
                      ? fileToUpload.name
                      : "Upload Resume File"}
                  </Button>
                </label>
                <Button
                  startIcon={savingResume ? <CircularProgress size={20} /> : <SaveIcon />}
                  variant="contained"
                  onClick={handleSave}
                  disabled={savingResume}
                  sx={{ mr: 1 }}
                >
                  {savingResume ? "Saving..." : "Save"}
                </Button>
                <Button
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>

          {!isEditing && selectedResume && (
            <ResumeVersions
              resume={selectedResume}
              onVersionsChange={handleVersionsChange}
            />
          )}

          <Box mb={4}>
            {isEditing ? (
              <>
                <TextField
                  fullWidth
                  label="Name"
                  value={displayedResume.personal_info.name}
                  onChange={(e) =>
                    handleFieldChange("personal_info", "name", e.target.value)
                  }
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Title"
                  value={displayedResume.personal_info.title}
                  onChange={(e) =>
                    handleFieldChange("personal_info", "title", e.target.value)
                  }
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  value={displayedResume.personal_info.email}
                  onChange={(e) =>
                    handleFieldChange("personal_info", "email", e.target.value)
                  }
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  value={displayedResume.personal_info.phone_number}
                  onChange={(e) =>
                    handleFieldChange(
                      "personal_info",
                      "phone_number",
                      e.target.value
                    )
                  }
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Location"
                  value={displayedResume.personal_info.location}
                  onChange={(e) =>
                    handleFieldChange(
                      "personal_info",
                      "location",
                      e.target.value
                    )
                  }
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="LinkedIn"
                  value={displayedResume.personal_info.linkedin}
                  onChange={(e) =>
                    handleFieldChange(
                      "personal_info",
                      "linkedin",
                      e.target.value
                    )
                  }
                  sx={{ mb: 2 }}
                />
              </>
            ) : (
              <>
                <Typography variant="h4" gutterBottom>
                  {displayedResume.personal_info.name}
                </Typography>
                <Typography variant="h6" gutterBottom color="text.secondary">
                  {displayedResume.personal_info.title}
                </Typography>
                <Typography variant="body1">
                  {displayedResume.personal_info.email}
                </Typography>
                <Typography variant="body1">
                  {displayedResume.personal_info.phone_number}
                </Typography>
                <Typography variant="body1">
                  {displayedResume.personal_info.location}
                </Typography>
                <Typography variant="body1">
                  {displayedResume.personal_info.linkedin}
                </Typography>
              </>
            )}
          </Box>

          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              Profile
            </Typography>
            <Divider />
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={Infinity}
                label="Profile"
                value={displayedResume.profile}
                onChange={(e) =>
                  handleFieldChange("profile", "", e.target.value)
                }
                sx={{ mt: 2 }}
              />
            ) : (
              <Typography variant="body1" sx={{ mt: 2 }}>
                {displayedResume.profile}
              </Typography>
            )}
          </Box>

          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              Experience
            </Typography>
            <Divider />
            <List>
              {displayedResume.experience.map((exp) => (
                <ListItem key={exp.id}>
                  {isEditing ? (
                    <Box sx={{ width: "100%" }}>
                      <Box display="flex" justifyContent="flex-end" mb={1}>
                        <IconButton
                          onClick={() => handleRemoveExperience(exp.id)}
                          color="error"
                          size="small"
                        >
                          <CancelIcon />
                        </IconButton>
                      </Box>
                      <TextField
                        fullWidth
                        label="Company"
                        value={exp.company}
                        onChange={(e) =>
                          handleExperienceChange(
                            exp.id,
                            "company",
                            e.target.value
                          )
                        }
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Position"
                        value={exp.position}
                        onChange={(e) =>
                          handleExperienceChange(
                            exp.id,
                            "position",
                            e.target.value
                          )
                        }
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Duration"
                        value={exp.duration}
                        onChange={(e) =>
                          handleExperienceChange(
                            exp.id,
                            "duration",
                            e.target.value
                          )
                        }
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={Infinity}
                        label="Description"
                        value={exp.description}
                        onChange={(e) =>
                          handleExperienceChange(
                            exp.id,
                            "description",
                            e.target.value
                          )
                        }
                      />
                    </Box>
                  ) : (
                    <ListItemText
                      primary={`${exp.position} at ${exp.company}`}
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {exp.duration}
                          </Typography>
                          <br />
                          {exp.description && typeof exp.description === 'string' 
                            ? exp.description.split("\n").map((line, index) => (
                                <React.Fragment key={index}>
                                  {line}
                                  <br />
                                </React.Fragment>
                              ))
                            : exp.description || ''}
                        </>
                      }
                    />
                  )}
                </ListItem>
              ))}
              {isEditing && (
                <ListItem>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleAddExperience}
                    startIcon={<AddIcon />}
                    sx={{ mt: 2 }}
                  >
                    Add Experience
                  </Button>
                </ListItem>
              )}
            </List>
          </Box>

          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              Education
            </Typography>
            <Divider />
            <List>
              {displayedResume.education.map((edu) => (
                <ListItem key={edu.id}>
                  {isEditing ? (
                    <Box sx={{ width: "100%" }}>
                      <TextField
                        fullWidth
                        label="Institution"
                        value={edu.institution}
                        onChange={(e) =>
                          handleEducationChange(
                            edu.id,
                            "institution",
                            e.target.value
                          )
                        }
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Degree"
                        value={edu.degree}
                        onChange={(e) =>
                          handleEducationChange(
                            edu.id,
                            "degree",
                            e.target.value
                          )
                        }
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Year"
                        value={edu.year}
                        onChange={(e) =>
                          handleEducationChange(edu.id, "year", e.target.value)
                        }
                      />
                    </Box>
                  ) : (
                    <ListItemText
                      primary={edu.institution}
                      secondary={`${edu.degree} - ${edu.year}`}
                    />
                  )}
                </ListItem>
              ))}
            </List>
          </Box>

          <Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h5" gutterBottom>
                Skills
              </Typography>
              {isEditing && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddSkill}
                  startIcon={<AddIcon />}
                >
                  Add Skill Category
                </Button>
              )}
            </Box>
            <Divider />
            <List>
              {displayedResume.skillset.map((skill) => (
                <ListItem key={skill.id}>
                  {isEditing ? (
                    <Box sx={{ width: "100%" }}>
                      <Box display="flex" gap={2}>
                        <TextField
                          label="Category"
                          value={skill.category}
                          onChange={(e) =>
                            handleSkillChange(
                              skill.id,
                              "category",
                              e.target.value
                            )
                          }
                          sx={{ width: "30%" }}
                        />
                        <TextField
                          label="Skills (comma-separated)"
                          value={skill.skills}
                          onChange={(e) =>
                            handleSkillChange(
                              skill.id,
                              "skills",
                              e.target.value
                            )
                          }
                          sx={{ width: "70%" }}
                        />
                        <IconButton
                          onClick={() => handleRemoveSkill(skill.id)}
                          color="error"
                          size="small"
                          sx={{ height: "100%", alignSelf: "center" }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        {skill.category}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {skill.skills &&
                          skill.skills.split(", ").map((item, index) => (
                            <Typography
                              key={index}
                              component="span"
                              sx={{
                                display: "inline-block",
                                bgcolor: "primary.main",
                                color: "white",
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              {item}
                            </Typography>
                          ))}
                      </Box>
                    </Box>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>

          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              Additional Information
            </Typography>
            <Divider />
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={Infinity}
                label="Additional Information"
                value={displayedResume.additional_info || ""}
                onChange={(e) =>
                  handleFieldChange("additional_info", "", e.target.value)
                }
                sx={{ mt: 2 }}
                placeholder="Add any additional information, certifications, awards, or other relevant details"
              />
            ) : (
              displayedResume.additional_info && (
                <Typography variant="body1" sx={{ mt: 2 }}>
                  {displayedResume.additional_info
                    .split("\n")
                    .map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                </Typography>
              )
            )}
          </Box>

          {/* Bidder Visibility Section */}
          <Box sx={{ mt: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Bidder Visibility
            </Typography>
            <Divider />

            <Box sx={{ mt: 2 }}>
              {teamBidders.length > 0 ? (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Visible to bidder</InputLabel>
                  <Select
                    value={visibleToBidder}
                    onChange={(e) => handleBidderVisibilityChange(e.target.value)}
                    label="Visible to bidder"
                    disabled={savingBidderVisibility}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {teamBidders.map((bidder) => (
                      <MenuItem key={bidder._id} value={bidder._id}>
                        {bidder.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No bidders available. Add bidders to your team to share this resume.
                </Typography>
              )}

            </Box>
          </Box>

          {/* Gmail API Integration Section - Always visible */}
          <Box sx={{ mt: 3, mb: 4 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
            >
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" minWidth={0}>
                <SectionTitleWithInfo
                  title="Gmail API Integration"
                  tip="Connect your Gmail account to use Gmail API for sending emails. This provides better reliability and includes automatic PDF attachments."
                  variant="h6"
                />
                {gmailConnected && selectedResume?.gmail_forwarding_status?.forward_to && (
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    Forwarding to {selectedResume.gmail_forwarding_status.forward_to}
                  </Typography>
                )}
              </Box>
              {gmailConnected ? (
                <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                  <IconButton
                    size="small"
                    onClick={handleRefreshGmailStatus}
                    aria-label="Refresh Gmail status"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                  <Button
                    variant="contained"
                    size="small"
                    color={gmailStatusHovered ? "error" : "success"}
                    startIcon={
                      removingGmail ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : gmailStatusHovered ? (
                        <LinkOffIcon />
                      ) : (
                        <LinkIcon />
                      )
                    }
                    onMouseEnter={() => setGmailStatusHovered(true)}
                    onMouseLeave={() => setGmailStatusHovered(false)}
                    onClick={handleRemoveGmailCredentials}
                    disabled={removingGmail}
                    sx={{ minWidth: 118 }}
                  >
                    {removingGmail
                      ? "Removing..."
                      : gmailStatusHovered
                        ? "Disconnect"
                        : "Connected"}
                  </Button>
                </Box>
              ) : (
                <Chip
                  label="Not Connected"
                  color="default"
                  icon={<LinkOffIcon />}
                  sx={{ flexShrink: 0 }}
                />
              )}
            </Box>
            <Divider />

            {gmailConnected ? (
              isTokenNearExpiry() && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Your Gmail connection will expire soon. Please reconnect
                      your Gmail account to ensure uninterrupted service.
                    </Typography>
                  </Alert>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<GoogleIcon />}
                    onClick={handleGenerateAuthUrl}
                  >
                    Reconnect Gmail
                  </Button>
                </Box>
              )
            ) : (
              <Box sx={{ mt: 2 }}>
                {/* Google Token Selection */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select Google OAuth Token</InputLabel>
                  <Select
                    value={selectedGoogleToken}
                    onChange={(e) => setSelectedGoogleToken(e.target.value)}
                    label="Select Google OAuth Token"
                    disabled={loadingGoogleTokens}
                  >
                    {availableGoogleTokens.map((token) => (
                      <MenuItem key={token.index} value={token.index}>
                        {token.label} (Token {token.index + 1})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {availableGoogleTokens.length === 0 && !loadingGoogleTokens && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      No Google OAuth tokens available. Please contact your administrator.
                    </Typography>
                  </Alert>
                )}

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<GoogleIcon />}
                    onClick={handleGenerateAuthUrl}
                    disabled={generatingAuthUrl || availableGoogleTokens.length === 0}
                  >
                    {generatingAuthUrl
                      ? "Generating..."
                      : "Connect Gmail Account"}
                  </Button>
                  <Button variant="outlined" onClick={handleRefreshGmailStatus}>
                    Refresh Status
                  </Button>
                </Box>
              </Box>
            )}
          </Box>

          {/* Automatic Resume Customization Section */}
          <Box mb={4}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <SectionTitleWithInfo
                title="Automatic Resume Customization"
                tip="When enabled, the extension automatically generates a tailored resume when filling applications on indexed job pages that have a stored job description. If automatic email applications are also enabled, jobs with at least one tech stack receive individually tailored cover letters, and jobs with a specific developer role can receive a tailored resume attachment as well."
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={autoCustomizeResume}
                    onChange={(e) => handleAutoCustomizeToggle(e.target.checked)}
                    color="primary"
                    disabled={savingAutoCustomizeResume}
                  />
                }
                label=""
              />
            </Box>
            <Divider />
          </Box>

          {/* Automatic Gmail Management Section */}
          <Box mb={4}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <SectionTitleWithInfo
                title="Automatic Gmail Management"
                tip="When enabled, your connected Gmail inbox is automatically processed. Unnecessary emails such as application confirmations are filtered, while interview invitations, action-required messages, and other important emails are categorized and kept."
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={gmailCleanup}
                    onChange={(e) => handleGmailCleanupToggle(e.target.checked)}
                    color="primary"
                    disabled={savingGmailCleanup}
                  />
                }
                label=""
              />
            </Box>
            <Divider />
            {cleanupError && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">
                  <Typography variant="body2">{cleanupError}</Typography>
                </Alert>
              </Box>
            )}
          </Box>

          {/* Automatic Email Applications Section */}
          <Box mb={4}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <SectionTitleWithInfo
                title="Automatic Email Applications"
                tip="When enabled, matching job opportunities from your searches are automatically scheduled and sent as email applications using your connected Gmail account, cover letter, and configured filters."
              />
              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FilterListIcon />}
                  onClick={handleOpenFiltersDialog}
                  disabled={!autoEmailEnabled}
                  sx={{
                    backgroundColor: "primary.main",
                    "&:hover": { backgroundColor: "primary.dark" },
                  }}
                >
                  Set Filters
                </Button>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoEmailEnabled}
                      onChange={(e) => handleAutoEmailToggle(e.target.checked)}
                      color="primary"
                      disabled={savingAutoEmail}
                    />
                  }
                  label=""
                />
              </Box>
            </Box>
            <Divider />

            {autoEmailEnabled && (
              <>
                {/* Next Scheduled Email Section */}
                <Box sx={{ mt: 3 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Typography variant="h6" gutterBottom>
                      Next Scheduled Email Applications
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={fetchScheduledEmailApplications}
                      disabled={loadingScheduledEmails}
                      startIcon={
                        loadingScheduledEmails ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ScheduleIcon />
                        )
                      }
                    >
                      Refresh
                    </Button>
                  </Box>

                  {scheduledEmailsError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body2">{scheduledEmailsError}</Typography>
                    </Alert>
                  )}

                  {scheduledEmails && (scheduledEmails.queueItems?.length ?? 0) > 0 ? (
                    <Box 
                      sx={{ 
                        maxHeight: '400px', 
                        maxWidth: '100%',
                        overflowY: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1
                      }}
                    >
                      <Paper sx={{ p: 3, mb: 2 }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={2}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <ScheduleIcon color="primary" />
                            <Typography variant="h6">
                              Queued Auto-Email Applications
                            </Typography>
                          </Box>
                          <Chip
                            label={`${scheduledEmails.queueItems.length} queued`}
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                        <List dense>
                          {scheduledEmails.queueItems.map((item) => (
                            <ListItem key={item.id} sx={{ pl: 0, alignItems: "flex-start" }}>
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <EmailIcon fontSize="small" color="action" />
                                    <Typography variant="body2">
                                      {item.title || "Scheduled application"}
                                    </Typography>
                                    <Chip
                                      label={item.status || "pending"}
                                      size="small"
                                      color={
                                        item.status === "sending" || item.status === "composing"
                                          ? "warning"
                                          : "default"
                                      }
                                      variant="outlined"
                                    />
                                  </Box>
                                }
                                secondary={
                                  <Box sx={{ mt: 0.5 }}>
                                    {item.company && (
                                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                        <BusinessIcon fontSize="small" color="action" />
                                        <Typography variant="caption" color="text.secondary">
                                          {item.company}
                                        </Typography>
                                      </Box>
                                    )}
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      <strong>Email:</strong> {item.applicationEmail}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      <strong>Scheduled for:</strong>{" "}
                                      {item.scheduledTime
                                        ? new Date(item.scheduledTime).toLocaleString()
                                        : "—"}
                                    </Typography>
                                    {item.url && (
                                      <Typography
                                        variant="caption"
                                        color="primary"
                                        component="a"
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        display="block"
                                        sx={{ mt: 0.5 }}
                                      >
                                        View job posting
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      No queued email applications yet. Matching jobs will appear here after search runs complete.
                    </Typography>
                  )}
                </Box>
              </>
            )}

            {/* Cover Letter Section */}
            <Box sx={{ mt: 3 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <SectionTitleWithInfo
                  title="Cover Letter"
                  tip="Enable automatic email applications above to send this cover letter with scheduled applications."
                  variant="h6"
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleGenerateCoverLetter}
                  disabled={generatingCoverLetter || !selectedResume}
                  startIcon={
                    generatingCoverLetter ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AutoAwesomeIcon />
                    )
                  }
                >
                  {generatingCoverLetter ? "Generating..." : "Generate Cover Letter"}
                </Button>
              </Box>

              <TextField
                fullWidth
                label="Cover Letter Title"
                value={coverLetterTitle}
                onChange={(e) => setCoverLetterTitle(e.target.value)}
                placeholder="e.g., Application for Software Engineer Position"
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                multiline
                minRows={6}
                maxRows={Infinity}
                label="Cover Letter Content"
                value={coverLetterContent}
                onChange={(e) => setCoverLetterContent(e.target.value)}
                placeholder="Write your cover letter content here."
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={handleSaveAutoEmailSettings}
                  disabled={savingAutoEmailSettings}
                  startIcon={
                    savingAutoEmailSettings ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                >
                  {savingAutoEmailSettings
                    ? "Saving..."
                    : "Save All Settings"}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Add new Dialog for Customized Resumes */}
          <Dialog
            open={customizedResumesOpen}
            onClose={() => setCustomizedResumesOpen(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6">Customized Resumes</Typography>
                <IconButton onClick={() => setCustomizedResumesOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              {customizedResumesOpen && (
                <CustomizedResumes
                  key={selectedResume?._id ? String(selectedResume._id) : 'all-resumes'}
                  baseResumeId={selectedResume?._id}
                  dialogMode
                />
              )}
            </DialogContent>
          </Dialog>
        </Paper>
      )}

    </Box>
      )}

      {pageTab === 1 && (
        <Box sx={{ mt: 2 }}>
          <GmailMailboxesPanel />
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setResumeToDelete(null);
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to delete this resume? This action cannot be
            undone.
          </DialogContentText>
          {resumeToDelete?.resume?.gmail_status?.connected && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={migrateGmailOnDelete}
                  onChange={(e) => setMigrateGmailOnDelete(e.target.checked)}
                  color="primary"
                />
              }
              label={`Move Gmail (${resumeToDelete.resume.gmail_status?.email || resumeToDelete.resume.gmail_email || "connected account"}) to standalone cleanup before deleting`}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={groupDialogOpen} onClose={handleCloseGroupDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editingGroup ? "Edit Group" : "Create Group"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group name"
            fullWidth
            value={groupNameInput}
            onChange={(e) => setGroupNameInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSaveGroup();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGroupDialog}>Cancel</Button>
          <Button onClick={handleSaveGroup} variant="contained" disabled={savingGroup}>
            {savingGroup ? "Saving..." : editingGroup ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteGroupDialogOpen}
        onClose={() => setDeleteGroupDialogOpen(false)}
      >
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete group "{groupToDelete?.name}"? Resumes in this group will become ungrouped.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteGroupDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteGroupConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modify the Auth URL Dialog */}
      <Dialog
        open={showAuthDialog}
        onClose={() => setShowAuthDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <GoogleIcon color="primary" />
            Connect Gmail Account
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            To use Gmail API for sending emails, you need to authorize this
            application to access your Gmail account.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Please use the Gmail account that
              matches your resume email address:{" "}
              <strong>{selectedResume?.personal_info?.email}</strong>
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Copy the authorization URL below and open it in the browser profile
            where you're signed in with your Gmail account.
          </Typography>

          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              value={authUrl}
              variant="outlined"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(authUrl);
                      toast.success("URL copied to clipboard!");
                    }}
                    size="small"
                    sx={{
                      ml: 1,
                      color: "primary.main",
                      "&:hover": { color: "primary.dark" },
                    }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            After authorization, return to this page and click "Refresh Status"
            to see the updated connection status.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              setShowAuthDialog(false);
              await handleRefreshGmailStatus();
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filters Configuration Dialog */}
      <Dialog
        open={filtersDialogOpen}
        onClose={handleCancelFilters}
        maxWidth="md"
        fullWidth
        aria-labelledby="filters-dialog-title"
      >
        <DialogTitle id="filters-dialog-title" sx={{
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1
        }}>
          <FilterListIcon />
          Configure Email Filters
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Location Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
              <LocationOnIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Location Filter
            </Typography>
            <Grid container spacing={1}>
              {locationOptions.map((option) => (
                <Grid item xs={6} sm={4} key={option.value}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={tempFilters.location.includes(option.value)}
                        onChange={(e) => {
                          if (option.value === "all") {
                            // If "Anywhere" is selected, deselect all others
                            if (e.target.checked) {
                              setTempFilters(prev => ({
                                ...prev,
                                location: ["all"]
                              }));
                            } else {
                              setTempFilters(prev => ({
                                ...prev,
                                location: []
                              }));
                            }
                          } else {
                            // If a specific location is selected
                            if (e.target.checked) {
                              // Remove "all" and add the specific location
                              const newLocations = tempFilters.location.filter(loc => loc !== "all");
                              setTempFilters(prev => ({
                                ...prev,
                                location: [...newLocations, option.value]
                              }));
                            } else {
                              // Remove the specific location
                              const newLocations = tempFilters.location.filter(loc => loc !== option.value);
                              if (newLocations.length === 0) {
                                // If no locations left, default to "all"
                                setTempFilters(prev => ({
                                  ...prev,
                                  location: ["all"]
                                }));
                              } else {
                                setTempFilters(prev => ({
                                  ...prev,
                                  location: newLocations
                                }));
                              }
                            }
                          }
                        }}
                        size="small"
                      />
                    }
                    label={option.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Category Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
              <CategoryIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Category Filter
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempFilters.category.includes("all")}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTempFilters(prev => ({
                            ...prev,
                            category: ["all"]
                          }));
                        } else {
                          setTempFilters(prev => ({
                            ...prev,
                            category: []
                          }));
                        }
                      }}
                      size="small"
                    />
                  }
                  label="All Categories"
                />
              </Grid>
              {teamCategories.map((category) => (
                <Grid item xs={6} sm={4} key={category}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={tempFilters.category.includes(category)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Remove "all" if it exists and add the specific category
                            const newCategories = tempFilters.category.filter(cat => cat !== "all");
                            setTempFilters(prev => ({
                              ...prev,
                              category: [...newCategories, category]
                            }));
                          } else {
                            // Remove the specific category
                            const newCategories = tempFilters.category.filter(cat => cat !== category);
                            if (newCategories.length === 0) {
                              // If no categories selected, default to "all"
                              setTempFilters(prev => ({
                                ...prev,
                                category: ["all"]
                              }));
                            } else {
                              setTempFilters(prev => ({
                                ...prev,
                                category: newCategories
                              }));
                            }
                          }
                        }}
                        size="small"
                      />
                    }
                    label={category}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Behavior Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
              <PsychologyIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Behavior Filter
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempFilters.behavior.includes("all")}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Select all behaviors
                          setTempFilters(prev => ({
                            ...prev,
                            behavior: ["all"]
                          }));
                        } else {
                          // Deselect all
                          setTempFilters(prev => ({
                            ...prev,
                            behavior: []
                          }));
                        }
                      }}
                      size="small"
                    />
                  }
                  label="All Behaviors"
                />
              </Grid>
              {behaviorOptions.map((behavior) => (
                <Grid item xs={6} sm={4} key={behavior}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={tempFilters.behavior.includes(behavior)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Remove "all" if it exists and add the specific behavior
                            const newBehaviors = tempFilters.behavior.filter(b => b !== "all");
                            setTempFilters(prev => ({
                              ...prev,
                              behavior: [...newBehaviors, behavior]
                            }));
                          } else {
                            // Remove the specific behavior
                            const newBehaviors = tempFilters.behavior.filter(b => b !== behavior);
                            if (newBehaviors.length === 0) {
                              // If no behaviors left, default to "all"
                              setTempFilters(prev => ({
                                ...prev,
                                behavior: ["all"]
                              }));
                            } else {
                              setTempFilters(prev => ({
                                ...prev,
                                behavior: newBehaviors
                              }));
                            }
                          }
                        }}
                        size="small"
                      />
                    }
                    label={behavior}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Query Date Limit Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
              <ScheduleIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Query Date Limit
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Date Limit</InputLabel>
              <Select
                multiple
                value={tempFilters.dateLimit}
                onChange={(e) => {
                  setTempFilters(prev => ({
                    ...prev,
                    dateLimit: e.target.value
                  }));
                }}
                label="Date Limit"
              >
                {dateLimitOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleCancelFilters} 
            variant="outlined"
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveFilters} 
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ 
              backgroundColor: "primary.main",
              "&:hover": { backgroundColor: "primary.dark" }
            }}
          >
            Save Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume;
